type FacebookPixel = {
  init: (pixelId: string) => void;
  pageView: () => void;
  track: (event: string) => void;
};

const FACEBOOK_PIXEL_RECOVERY_KEY = 'facebook-pixel-chunk-reload';

let pixelPromise: Promise<FacebookPixel | null> | null = null;
let pixelInitialized = false;

function isChunkLoadError(error: unknown) {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';

  return message.includes('ChunkLoadError') || /Loading chunk \d+ failed/i.test(message);
}

function recoverFromChunkLoadError(error: unknown) {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;

  try {
    const storage = window.sessionStorage;
    if (storage.getItem(FACEBOOK_PIXEL_RECOVERY_KEY) === '1') return false;

    storage.setItem(FACEBOOK_PIXEL_RECOVERY_KEY, '1');
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

async function loadFacebookPixel() {
  if (typeof window === 'undefined') return null;

  if (!pixelPromise) {
    pixelPromise = import('react-facebook-pixel')
      .then((module) => {
        window.sessionStorage.removeItem(FACEBOOK_PIXEL_RECOVERY_KEY);
        return module.default as FacebookPixel;
      })
      .catch((error) => {
        if (!recoverFromChunkLoadError(error)) {
          console.warn('Failed to load Facebook Pixel', error);
        }

        return null;
      });
  }

  return pixelPromise;
}

export async function initFacebookPixel(pixelId: string) {
  const pixel = await loadFacebookPixel();
  if (!pixel || !pixelId || pixelInitialized) return false;

  pixel.init(pixelId);
  pixelInitialized = true;
  return true;
}

export async function trackFacebookPageView() {
  const pixel = await loadFacebookPixel();
  if (!pixel || !pixelInitialized) return false;

  pixel.pageView();
  return true;
}

export async function trackFacebookEvent(event: string) {
  const pixel = await loadFacebookPixel();
  if (!pixel || !pixelInitialized) return false;

  pixel.track(event);
  return true;
}
